import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDays,
  faBolt,
  faBrain,
  faChevronLeft,
  faChevronRight,
  faChartColumn,
  faCheckCircle,
  faFireFlameCurved,
  faHeartPulse,
  faLayerGroup,
  faPenToSquare,
  faPlus,
  faStar,
} from "@fortawesome/free-solid-svg-icons";
import HabitModal from "../components/habits/HabitModal";
import { createHabit, getHabitsForDate, getHabitsHeatmap, getHabitsSummary, toggleHabit, toYmd, updateHabit } from "../api/habitsApi";
import "./HabitosPage.css";

const RANGE_OPTIONS = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "1y", label: "Ano" },
];

const PERIOD_GROUPS = [
  { value: "manha", label: "Manha" },
  { value: "tarde", label: "Tarde" },
  { value: "noite", label: "Noite" },
  { value: "qualquer_horario", label: "Qualquer horario" },
];

const PILLAR_META = {
  energia: { label: "Energia", weight: 25, icon: faBolt },
  foco: { label: "Foco", weight: 35, icon: faStar },
  clareza: { label: "Clareza", weight: 20, icon: faBrain },
  saude_mental: { label: "Saude mental", weight: 20, icon: faHeartPulse },
  execucao: { label: "Execucao", weight: 25, icon: faCheckCircle },
};

const getPillarMeta = (pillar) => PILLAR_META[pillar] || { label: "Geral", weight: 10, icon: faLayerGroup };

const formatPeriod = (period) =>
  PERIOD_GROUPS.find((item) => item.value === period)?.label || "Qualquer horario";

const getDoneFeedback = (habit) => {
  const pillar = habit?.pilar || "";
  const messages = {
    energia: "Boa. Energia bem cuidada sustenta o resto do dia.",
    foco: "Excelente. Cada repeticao fortalece sua atencao.",
    clareza: "Boa escolha. Clareza reduz atrito antes da execucao.",
    saude_mental: "Muito bom. Regular o estado mental tambem e performance.",
    execucao: "Feito. Pequenas entregas repetidas constroem tracao.",
  };
  return messages[pillar] || "Habito concluido. Consistencia vence intensidade isolada.";
};

const intensityClass = (value) => {
  if (value >= 100) return "full";
  if (value >= 66) return "high";
  if (value >= 33) return "medium";
  if (value > 0) return "low";
  return "empty";
};

const formatLongDate = (date) =>
  date.toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

const formatShortDate = (dateInput) => {
  const date = new Date(dateInput);
  if (Number.isNaN(date.getTime())) return String(dateInput || "");
  return date.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
};

const normalizeSeries = (series = []) =>
  series.map((point, index) => {
    const date = point?.data || point?.date || point?.dia || null;
    return {
      label: point?.label || (date ? formatShortDate(date) : `D${index + 1}`),
      value: Math.max(0, Math.min(100, Number(point?.percentual ?? point?.percent ?? point?.valor ?? 0))),
    };
  });

const buildLinePoints = (series, width = 640, height = 190) => {
  if (!series.length) return "";
  const step = series.length > 1 ? width / (series.length - 1) : 0;
  return series
    .map((point, index) => {
      const x = index * step;
      const y = height - (point.value / 100) * height;
      return `${x},${y}`;
    })
    .join(" ");
};

const buildYearMatrix = (records, year) => {
  const start = new Date(year, 0, 1);
  const end = new Date(year, 11, 31);
  const first = new Date(start);
  first.setDate(start.getDate() - start.getDay());
  const recordMap = new Map(
    (records || []).map((day) => [String(day?.data || day?.date || "").slice(0, 10), Number(day?.percentual ?? 0)])
  );
  const weeks = [];
  const cursor = new Date(first);

  while (cursor <= end || cursor.getDay() !== 0) {
    const week = [];
    for (let weekday = 0; weekday < 7; weekday += 1) {
      const key = cursor.toISOString().slice(0, 10);
      const inYear = cursor.getFullYear() === year;
      week.push({
        key,
        date: new Date(cursor),
        inYear,
        value: inYear ? recordMap.get(key) || 0 : null,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

const HabitosPage = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [range, setRange] = useState("7d");
  const [year, setYear] = useState(new Date().getFullYear());
  const [habits, setHabits] = useState([]);
  const [summary, setSummary] = useState({ completedToday: 0, totalToday: 0, currentStreak: 0, consistency: 0, series: [] });
  const [heatmap, setHeatmap] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHeatmap, setLoadingHeatmap] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHabit, setEditingHabit] = useState(null);
  const [savingHabit, setSavingHabit] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadPage = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const [habitRecords, habitSummary] = await Promise.all([
        getHabitsForDate(selectedDate),
        getHabitsSummary(range, selectedDate),
      ]);

      setHabits(habitRecords);
      setSummary({
        ...habitSummary,
        totalToday: habitSummary.totalToday || habitRecords.length,
        completedToday: habitSummary.completedToday || habitRecords.filter((habit) => habit.concluido).length,
      });
    } catch (error) {
      console.error("HabitosPage: erro ao carregar habitos", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel carregar os habitos."
      );
      setHabits([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHeatmap = async () => {
    try {
      setLoadingHeatmap(true);
      const days = await getHabitsHeatmap(year);
      setHeatmap(Array.isArray(days) ? days : []);
    } catch (error) {
      console.error("HabitosPage: erro ao carregar heatmap", error);
      setHeatmap([]);
    } finally {
      setLoadingHeatmap(false);
    }
  };

  useEffect(() => {
    loadPage();
  }, [selectedDate, range]);

  useEffect(() => {
    loadHeatmap();
  }, [year]);

  const completionRatio = useMemo(() => {
    const total = summary.totalToday || habits.length || 0;
    if (!total) return 0;
    return Math.round(((summary.completedToday || habits.filter((habit) => habit.concluido).length) / total) * 100);
  }, [habits, summary]);

  const performanceScore = useMemo(() => {
    const totalWeight = habits.reduce((acc, habit) => acc + getPillarMeta(habit.pilar).weight, 0);
    if (!totalWeight) return 0;
    const doneWeight = habits
      .filter((habit) => habit.concluido)
      .reduce((acc, habit) => acc + getPillarMeta(habit.pilar).weight, 0);
    return Math.round((doneWeight / totalWeight) * 100);
  }, [habits]);

  const habitsByPeriod = useMemo(() => {
    const grouped = PERIOD_GROUPS.map((period) => ({
      ...period,
      habits: habits.filter((habit) => (habit.periodo_dia || "qualquer_horario") === period.value),
    }));

    const knownPeriods = new Set(PERIOD_GROUPS.map((period) => period.value));
    const others = habits.filter((habit) => !knownPeriods.has(habit.periodo_dia || "qualquer_horario"));
    return others.length ? [...grouped, { value: "outros", label: "Outros", habits: others }] : grouped;
  }, [habits]);

  const weeklyReview = useMemo(() => {
    const series = normalizeSeries(summary.series || []).slice(-7);

    const average = series.length
      ? Math.round(series.reduce((acc, point) => acc + point.value, 0) / series.length)
      : 0;
    const bestDay = series.reduce((best, point) => (!best || point.value > best.value ? point : best), null);
    const weakestDay = series.reduce((weakest, point) => (!weakest || point.value < weakest.value ? point : weakest), null);
    const strongestHabit = habits.reduce((best, habit) => (!best || Number(habit.streak_atual || 0) > Number(best.streak_atual || 0) ? habit : best), null);
    const nextAdjustment = habits.find((habit) => !habit.concluido) || null;

    return { average, bestDay, weakestDay, strongestHabit, nextAdjustment };
  }, [habits, summary.series]);

  const chartSeries = useMemo(() => normalizeSeries(summary.series || []), [summary.series]);
  const chartPoints = useMemo(() => buildLinePoints(chartSeries), [chartSeries]);
  const yearMatrix = useMemo(() => buildYearMatrix(heatmap, year), [heatmap, year]);

  const handleToggleHabit = async (habit) => {
    const nextDone = !habit.concluido;
    try {
      setErrorMessage("");
      setSuccessMessage("");
      await toggleHabit(habit.id, {
        data: toYmd(selectedDate),
        concluido: nextDone,
      });
      await loadPage();
      setSuccessMessage(nextDone ? getDoneFeedback(habit) : "Tudo bem ajustar a rota. Volte para o proximo passo pequeno.");
    } catch (error) {
      console.error("HabitosPage: erro ao marcar habito", error);
      setErrorMessage("Nao foi possivel atualizar o habito.");
    }
  };

  const handleSaveHabit = async (payload) => {
    try {
      setSavingHabit(true);
      setErrorMessage("");
      setSuccessMessage("");

      if (editingHabit) {
        await updateHabit(editingHabit.id, payload);
        setSuccessMessage("Habito atualizado com sucesso.");
      } else {
        await createHabit(payload);
        setSuccessMessage("Habito criado com sucesso.");
      }

      setShowModal(false);
      setEditingHabit(null);
      await loadPage();
      await loadHeatmap();
    } catch (error) {
      console.error("HabitosPage: erro ao salvar habito", error);
      setErrorMessage(
        error?.response?.data?.message ||
          error?.message ||
          "Nao foi possivel salvar o habito."
      );
    } finally {
      setSavingHabit(false);
    }
  };

  return (
    <>
      <Container fluid className="habitos-page">
        <section className="habitos-hero">
          <div>
            <p className="habitos-kicker">Consistency system</p>
            <h1>Habitos</h1>
            <p className="habitos-subtitle">
              Construa consistencia com uma visao diaria simples, historico de progresso e ajuste rapido do que realmente sustenta a rotina.
            </p>
          </div>

          <div className="habitos-hero-actions">
            <Button variant="light" onClick={() => setSelectedDate(new Date())}>
              Hoje
            </Button>
            <Button
              variant="outline-light"
              className="habitos-primary-action"
              onClick={() => {
                setEditingHabit(null);
                setShowModal(true);
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> Novo Habito
            </Button>
          </div>
        </section>

        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        {successMessage && <Alert variant="success">{successMessage}</Alert>}

        <Row className="g-4">
          <Col xl={5}>
            <Card className="habitos-panel h-100">
              <Card.Body>
                <div className="habitos-panel-header">
                  <div>
                    <h4>
                      <FontAwesomeIcon icon={faFireFlameCurved} /> Habitos do dia
                    </h4>
                    <p>{formatLongDate(selectedDate)}</p>
                  </div>

                  <div className="habitos-date-nav">
                    <Button variant="light" size="sm" onClick={() => setSelectedDate((current) => new Date(current.getFullYear(), current.getMonth(), current.getDate() - 1))}>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>
                    <Button variant="light" size="sm" onClick={() => setSelectedDate((current) => new Date(current.getFullYear(), current.getMonth(), current.getDate() + 1))}>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                  </div>
                </div>

                {loading ? (
                  <div className="habitos-empty-state">
                    <Spinner size="sm" className="me-2" />
                    Carregando habitos...
                  </div>
                ) : habits.length === 0 ? (
                  <div className="habitos-empty-state">
                    Nenhum habito configurado ainda para este dia.
                  </div>
                ) : (
                  <div className="habitos-today-list">
                    {habitsByPeriod.map((group) => group.habits.length > 0 && (
                      <section key={group.value} className="habit-period-section">
                        <div className="habit-period-title">
                          <span>{group.label}</span>
                          <small>{group.habits.filter((habit) => habit.concluido).length}/{group.habits.length}</small>
                        </div>

                        {group.habits.map((habit) => {
                          const pillarMeta = getPillarMeta(habit.pilar);

                          return (
                      <article key={habit.id} className="habit-day-item">
                        <button
                          type="button"
                          className={`habit-day-check ${habit.concluido ? "checked" : ""}`}
                          onClick={() => handleToggleHabit(habit)}
                        />

                        <div className="habit-day-copy">
                          <strong>{habit.nome}</strong>
                          <small>
                            {habit.tipo === "meta_flexivel"
                              ? `${habit.meta_quantidade || 0} ${habit.meta_unidade || ""} por ${habit.periodo_meta || "dia"}`
                              : formatPeriod(habit.periodo_dia)}
                          </small>
                          <div className="habit-day-meta">
                            <span>
                              <FontAwesomeIcon icon={pillarMeta.icon} /> {pillarMeta.label}
                            </span>
                            {habit.habito_ancora && <span>Depois de: {habit.habito_ancora}</span>}
                          </div>
                        </div>

                        <div className="habit-day-actions">
                          <span className="habit-streak-chip">
                            <FontAwesomeIcon icon={faFireFlameCurved} /> {habit.streak_atual || 0}
                          </span>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={() => {
                              setEditingHabit(habit);
                              setShowModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faPenToSquare} />
                          </Button>
                        </div>
                      </article>
                          );
                        })}
                      </section>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={7}>
            <Card className="habitos-panel h-100">
              <Card.Body>
                <div className="habitos-panel-header">
                  <div>
                    <h4>
                      <FontAwesomeIcon icon={faChartColumn} /> Ritmo recente
                    </h4>
                    <p>Leia sua consistencia sem abrir planilha mental no meio do dia.</p>
                  </div>

                  <Badge bg="light" text="dark">{completionRatio}% hoje</Badge>
                </div>

                <div className="habitos-summary-cards">
                  <div className="habitos-summary-card">
                    <span>Feitos hoje</span>
                    <strong>{summary.completedToday || 0}/{summary.totalToday || habits.length}</strong>
                  </div>
                  <div className="habitos-summary-card accent">
                    <span>Score de performance</span>
                    <strong>{performanceScore}%</strong>
                  </div>
                  <div className="habitos-summary-card">
                    <span>Sequencia</span>
                    <strong>{summary.currentStreak || 0} dias</strong>
                  </div>
                  <div className="habitos-summary-card">
                    <span>Consistencia</span>
                    <strong>{Math.round(summary.consistency || 0)}%</strong>
                  </div>
                </div>

                <div className="habitos-weekly-review">
                  <div>
                    <span>Revisao semanal</span>
                    <strong>{weeklyReview.average}% de consistencia media</strong>
                  </div>
                  <div>
                    <span>Melhor dia</span>
                    <strong>{weeklyReview.bestDay ? `${weeklyReview.bestDay.label} (${Math.round(weeklyReview.bestDay.value)}%)` : "Sem dados"}</strong>
                  </div>
                  <div>
                    <span>Ponto de ajuste</span>
                    <strong>
                      {weeklyReview.nextAdjustment
                        ? `Retomar: ${weeklyReview.nextAdjustment.nome}`
                        : weeklyReview.weakestDay
                          ? `Revisar ${weeklyReview.weakestDay.label}`
                          : "Sem dados"}
                    </strong>
                  </div>
                  <div>
                    <span>Habito mais forte</span>
                    <strong>
                      {weeklyReview.strongestHabit
                        ? `${weeklyReview.strongestHabit.nome} (${weeklyReview.strongestHabit.streak_atual || 0} dias)`
                        : "Sem dados"}
                    </strong>
                  </div>
                </div>

                <div className="habitos-range-tabs">
                  {RANGE_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={range === option.value ? "active" : ""}
                      onClick={() => setRange(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>

                <div className="habitos-line-chart">
                  <div className="habitos-line-axis">
                    <span>100%</span>
                    <span>50%</span>
                    <span>0%</span>
                  </div>
                  <div className="habitos-line-plot">
                    <svg viewBox="0 0 640 190" preserveAspectRatio="none" aria-hidden="true">
                      <line x1="0" y1="0" x2="640" y2="0" />
                      <line x1="0" y1="95" x2="640" y2="95" />
                      <line x1="0" y1="190" x2="640" y2="190" />
                      {chartPoints && <polyline points={chartPoints} />}
                      {chartSeries.map((point, index) => {
                        const x = chartSeries.length > 1 ? (index * 640) / (chartSeries.length - 1) : 0;
                        const y = 190 - (point.value / 100) * 190;
                        return <circle key={`${point.label}-${index}`} cx={x} cy={y} r="4" />;
                      })}
                    </svg>
                    <div className="habitos-line-labels">
                      {chartSeries.map((point, index) => (
                        <span key={`${point.label}-${index}`}>{point.label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <section className="habitos-heatmap-section">
          <Card className="habitos-panel">
            <Card.Body>
              <div className="habitos-panel-header">
                <div>
                  <h4>
                    <FontAwesomeIcon icon={faCalendarDays} /> Consistencia {year}
                  </h4>
                  <p>Uma leitura anual para enxergar ritmo, buracos e picos de constancia.</p>
                </div>

                <div className="habitos-year-nav">
                  <Button variant="light" size="sm" onClick={() => setYear((current) => current - 1)}>
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>
                  <strong>{year}</strong>
                  <Button variant="light" size="sm" onClick={() => setYear((current) => current + 1)}>
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
              </div>

              {loadingHeatmap ? (
                <div className="habitos-empty-state">
                  <Spinner size="sm" className="me-2" />
                  Carregando consistencia...
                </div>
              ) : (
                <div className="habitos-github-heatmap">
                  {heatmap.length === 0 ? (
                    <div className="habitos-empty-state">Nenhum dado anual disponivel ainda.</div>
                  ) : (
                    <>
                      <div className="habitos-heatmap-weekdays">
                        <span>Dom</span>
                        <span>Seg</span>
                        <span>Ter</span>
                        <span>Qua</span>
                        <span>Qui</span>
                        <span>Sex</span>
                        <span>Sab</span>
                      </div>
                      <div className="habitos-heatmap-scroll">
                        <div className="habitos-heatmap-weeks">
                          {yearMatrix.map((week, weekIndex) => (
                            <div key={`week-${weekIndex}`} className="habitos-heatmap-week">
                              {week.map((day) => (
                                <div
                                  key={day.key}
                                  className={`habitos-heatmap-cell ${day.inYear ? intensityClass(day.value) : "outside"}`}
                                  title={day.inYear ? `${day.key} - ${Math.round(day.value || 0)}%` : ""}
                                />
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </section>
      </Container>

      <HabitModal
        show={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingHabit(null);
        }}
        habit={editingHabit}
        onSave={handleSaveHabit}
        saving={savingHabit}
      />
    </>
  );
};

export default HabitosPage;
